import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { NavLink, useLocation } from 'react-router-dom';
import {
  getAlertes,
  getEvenementsClients,
  getNotifications,
  marquerAlertesVues,
  marquerEvenementsVus,
  marquerPastillesVues,
} from '../services/sessionService';
import { getParametresDevis } from '../services/parametreDevisService';
import { useAuth } from '../context/AuthContext';
import logoClair from '../assets/logo-studmin-clair.png';
import '../assets/styles/Sidebar.css';
import { EVENEMENT_RECHERCHE } from './RechercheGlobale';

const NAV_ITEMS = [
  // Le calendrier ouvre le menu, Trello vient juste après : les deux s'ouvrent en début de journée.
  {
    to: '/calendrier',
    label: 'Calendrier',
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <rect x="3" y="5" width="18" height="16" rx="2" stroke="currentColor" strokeWidth="1.6" />
        <path d="M3 10h18M8 3v4M16 3v4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
        <path d="M7.5 14h2M11 14h2M14.5 14h2M7.5 17.5h2M11 17.5h2" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
      </svg>
    ),
  },
  // Trello, et non dans Paramétrage : c'est de là que les demandes
  // entrent, on l'ouvre avant de traiter la liste. Entrée simple, sans
  // sous-menu — comme Demande de devis juste dessous.
  {
    to: '/demande-de-devis/trello',
    label: 'Trello',
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <rect x="3" y="3" width="18" height="18" rx="2.5" stroke="currentColor" strokeWidth="1.6" />
        <rect x="6.5" y="6.5" width="4.5" height="11" rx="1" stroke="currentColor" strokeWidth="1.6" />
        <rect x="13" y="6.5" width="4.5" height="7" rx="1" stroke="currentColor" strokeWidth="1.6" />
      </svg>
    ),
  },
  {
    to: '/demande-de-devis',
    label: 'Demande de devis',
    // Route exacte : /demande-de-devis/trello est la page Trello, pas celle-ci.
    end: true,
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path
          d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6z"
          stroke="currentColor"
          strokeWidth="1.6"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path d="M14 2v6h6" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M8 13h8M8 17h8M8 9h2" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
  },
  {
    to: '/contrats',
    label: 'Contrat client',
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path
          d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6z"
          stroke="currentColor"
          strokeWidth="1.6"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path d="M14 2v6h6" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M8.5 16.5l2 2 4.5-5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
  },
  {
    to: '/convocations',
    label: 'Convocation',
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <rect x="2.5" y="5" width="19" height="14" rx="2" stroke="currentColor" strokeWidth="1.6" />
        <path d="M3 7l9 6 9-6" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
  },
  {
    to: '/rappels',
    label: 'Rappel',
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path
          d="M18 8a6 6 0 1 0-12 0c0 6-2 7-2 7h16s-2-1-2-7z"
          stroke="currentColor"
          strokeWidth="1.6"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path d="M10.3 19a2 2 0 0 0 3.4 0" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
      </svg>
    ),
  },
  {
    to: '/feuilles-de-presence',
    label: 'Feuille de présence',
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <rect x="4" y="3" width="16" height="18" rx="2" stroke="currentColor" strokeWidth="1.6" />
        <path d="M8 8h8M8 12h8M8 16h4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
      </svg>
    ),
  },
  {
    // Les certifications nominatives, générées avec la feuille de présence.
    to: '/attestations',
    label: 'Attestation',
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <circle cx="12" cy="9" r="5.5" stroke="currentColor" strokeWidth="1.6" />
        <path d="M12 6.6l.9 1.8 2 .3-1.45 1.4.35 2-1.8-.95-1.8.95.35-2L9.1 8.7l2-.3L12 6.6z" fill="currentColor" />
        <path d="M8.8 13.8L7.5 21l4.5-2.4L16.5 21l-1.3-7.2" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
  },
  {
    to: '/reporting',
    label: 'Reporting',
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M4 20V10M10 20V4M16 20v-7M22 20H2" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
  },
  {
    // Le journal des actions : qui a fait quoi, filtrable par utilisateur.
    to: '/historique',
    label: 'Historique',
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <circle cx="12" cy="12" r="8.5" stroke="currentColor" strokeWidth="1.6" />
        <path d="M12 7.5V12l3 2" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
  },
  {
    // Tout ce qui a été supprimé depuis 30 jours : restaurable, jamais effacé.
    to: '/corbeille',
    label: 'Corbeille',
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M4 7h16M9 7V4.8c0-.4.3-.8.8-.8h4.4c.5 0 .8.4.8.8V7M6.5 7l.8 12.2c0 .5.4.8.9.8h7.6c.5 0 .9-.3.9-.8L17.5 7" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M10 11v5M14 11v5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
      </svg>
    ),
  },
];

/**
 * Les formateurs, rangés après les clients.
 *
 * Ils suivaient la feuille de présence, au milieu du parcours d'un dossier.
 * Mais leurs pages ne jalonnent pas ce parcours : elles s'adressent à des
 * gens, comme celles des clients. Elles se lisent donc à la suite, en bas
 * du menu, plutôt qu'en travers des six étapes.
 */
const FORMATEURS_ITEM = {
    to: '/formateurs',
    label: 'Formateurs',
    // Tout ce qui concerne les formateurs, sous une même entrée : leurs
    // fiches, puis les documents qui leur sont adressés ou qu'ils signent.
    sousMenu: [
      { value: '/formateurs', label: 'Liste des formateurs', end: true },
      { value: '/formateurs/contrats', label: 'Contrats des formateurs' },
      { value: '/formateurs/convocations', label: 'Convocations formateurs' },
      { value: '/formateurs/rappels', label: 'Rappels formateurs' },
      { value: '/formateurs/feuilles-de-presence', label: 'Feuilles de présence' },
      // Écrire à plusieurs formateurs (avec leurs accès), et leur ranger des supports.
      { value: '/formateurs/messagerie', label: 'Messagerie' },
      { value: '/formateurs/espace-pedagogique', label: 'Espace pédagogique' },
    ],
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <circle cx="9" cy="8" r="3.2" stroke="currentColor" strokeWidth="1.6" />
        <path d="M3.5 19c.6-3.2 2.8-5 5.5-5s4.9 1.8 5.5 5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
        <path d="M15.5 4.8h5M15.5 8h5M15.5 11.2h3" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
      </svg>
    ),
  };

// Le QCM de recrutement : les deux banques (clients, formateurs) sont des
// onglets DANS la page — une seule entrée ici, après les personnes qu'elle
// évalue.
const QCM_ITEM = {
  to: '/qcm',
  label: 'QCM',
  icon: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect x="3" y="3" width="18" height="18" rx="2.5" stroke="currentColor" strokeWidth="1.6" />
      <path d="M7.5 8.5l1.5 1.5 2.5-3" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M13.5 8.5H17" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
      <path d="M7.5 15.5l1.5 1.5 2.5-3" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M13.5 15.5H17" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    </svg>
  ),
};

// Signature et Contrat sont les deux pages tournées vers le client : elles
// sont regroupées sous « Clients » plutôt que posées à plat dans PILOTAGE.
const CLIENTS_OPTIONS = [
  // `badge` : la clé de notification que cette entrée porte, en plus du menu.
  { value: '/clients', label: 'Liste des clients' },
  { value: '/signature-electronique', label: 'Signature devis' },
  { value: '/signature-contrat', label: 'Signature contrat' },
  // Les signatures posées par les clients depuis les liens publics
  // d'émargement : qui a signé, quand, sur quelle séance.
  { value: '/signatures-recues', label: 'Signatures reçues' },
];

// « Emails » n'y figure pas : chaque page porte le réglage de son propre
// email, au plus près de l'envoi qu'il gouverne.
const PARAMETRAGE_OPTIONS = [
  { value: '/parametrage/devis', label: 'Devis' },
  { value: '/parametrage/formations', label: 'Formations' },
  { value: '/parametrage/rappels', label: 'Rappels' },
  { value: '/parametrage/documents', label: 'Documents à apporter' },
  { value: '/parametrage/modeles-contrat', label: 'Modèles de contrat' },
  { value: '/parametrage/acces', label: 'Accès' },
];

const GEAR_ICON = (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6z"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 1 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 1 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.6a1.65 1.65 0 0 0 1-1.51V3a2 2 0 1 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 1 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"
      stroke="currentColor"
      strokeWidth="1.4"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const CLIENTS_ICON = (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <circle cx="9" cy="7" r="4" stroke="currentColor" strokeWidth="1.6" />
    <path
      d="M22 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const CHEVRON_ICON = (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

/**
 * Entrée de menu dépliante, partagée par « Clients » et « Paramétrage ».
 *
 * Sous-menu maison plutôt qu'un <select> : le menu déroulant natif est rendu
 * par le système, impossible à habiller comme le reste de la barre.
 *
 * `options` doit être une constante de module : la référence sert de
 * dépendance d'effet, une liste reconstruite à chaque rendu rouvrirait le
 * menu en boucle.
 */
function MenuDeplie({ libelle, icone, options, badge = 0, badges = {} }) {
  const { pathname } = useLocation();
  const actif = options.find((opt) => pathname.startsWith(opt.value));

  // Déplié d'office quand on est déjà sur une de ses pages : la page courante
  // doit se voir dans le menu, sans avoir à l'ouvrir.
  const [deplie, setDeplie] = useState(Boolean(actif));

  useEffect(() => {
    if (actif) setDeplie(true);
  }, [actif]);

  return (
    <li>
      <button
        type="button"
        className={
          'sidebar-link sidebar-link-bouton' + (actif && !deplie ? ' sidebar-link-active' : '')
        }
        onClick={() => setDeplie((v) => !v)}
        aria-expanded={deplie}
      >
        <span className="sidebar-link-icon">{icone}</span>
        <span>{libelle}</span>
        {badge > 0 && (
          <span className="sidebar-notif" title={`${badge} signature${badge > 1 ? 's' : ''} à voir`}>
            {badge}
          </span>
        )}
        <span
          className={'sidebar-chevron' + (deplie ? ' sidebar-chevron-deplie' : '')}
          aria-hidden="true"
        >
          {CHEVRON_ICON}
        </span>
      </button>

      {deplie && (
        <ul className="sidebar-sousmenu">
          {options.map((opt) => (
            <li key={opt.value}>
              <NavLink
                to={opt.value}
                className={({ isActive }) =>
                  'sidebar-souslien' + (isActive ? ' sidebar-souslien-actif' : '')
                }
              >
                {opt.label}
                {/* La pastille du menu se répète sur l'entrée qui y mène :
                    une fois le menu déplié, on doit voir où aller. */}
                {opt.badge && badges[opt.badge] > 0 && (
                  <span
                    className="sidebar-notif"
                    title={`${badges[opt.badge]} à voir`}
                  >
                    {badges[opt.badge]}
                  </span>
                )}
              </NavLink>
            </li>
          ))}
        </ul>
      )}
    </li>
  );
}

/**
 * Entrée du parcours : un lien navigable, éventuellement doublé d'un
 * sous-menu.
 *
 * Distincte de MenuDeplie, dont l'entête est un simple bouton : ici la page
 * parente existe et doit rester atteignable d'un clic. Le chevron est donc un
 * bouton séparé, posé à côté du lien — l'imbriquer dans le <a> produirait un
 * contrôle interactif dans un lien, que le HTML n'autorise pas.
 */
function EntreeParcours({ item }) {
  const { pathname } = useLocation();
  const sousMenu = item.sousMenu || [];
  const enfantActif = sousMenu.some((opt) => pathname.startsWith(opt.value));
  // Quand une entrée du sous-menu reprend la route parente, le parent ne
  // s'allume que sur cette route exacte : sur une sous-page, c'est l'entrée
  // du sous-menu qui dit où l'on est.
  const parentExact = sousMenu.some((opt) => opt.value === item.to);

  const [deplie, setDeplie] = useState(enfantActif);

  useEffect(() => {
    if (enfantActif) setDeplie(true);
  }, [enfantActif]);

  return (
    <li className={sousMenu.length ? 'sidebar-entree' : undefined}>
      <NavLink
        to={item.to}
        end={parentExact || Boolean(item.end)}
        className={({ isActive }) => 'sidebar-link' + (isActive ? ' sidebar-link-active' : '')}
      >
        <span className="sidebar-link-icon">{item.icon}</span>
        <span>{item.label}</span>
        {item.badge > 0 && (
          <span
            className="sidebar-notif"
            title={`${item.badge} à traiter`}
          >
            {item.badge}
          </span>
        )}
      </NavLink>

      {sousMenu.length > 0 && (
        <>
          <button
            type="button"
            className={
              'sidebar-chevron-bouton' + (deplie ? ' sidebar-chevron-deplie' : '')
            }
            onClick={() => setDeplie((v) => !v)}
            aria-expanded={deplie}
            aria-label={deplie ? 'Replier' : 'Déplier'}
          >
            {CHEVRON_ICON}
          </button>

          {deplie && (
            <ul className="sidebar-sousmenu">
              {sousMenu.map((opt) => (
                <li key={opt.value}>
                  <NavLink
                    to={opt.value}
                    end={Boolean(opt.end)}
                    className={({ isActive }) =>
                      'sidebar-souslien' + (isActive ? ' sidebar-souslien-actif' : '')
                    }
                  >
                    {opt.label}
                  </NavLink>
                </li>
              ))}
            </ul>
          )}
        </>
      )}
    </li>
  );
}

/**
 * `ouvert` et `onFermer` ne servent qu'au format tiroir (petits écrans) :
 * au-delà du point de rupture, la barre est toujours visible et la classe
 * comme le bouton de fermeture n'ont plus d'effet.
 */
// Route -> numéro d'étape, dérivé du référentiel : la sidebar affiche les
// mêmes chiffres que la barre d'avancement, sans les redéclarer.

const CLOCHE_ICON = (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M6 16V11a6 6 0 1112 0v5l1.5 2h-15L6 16z"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinejoin="round"
    />
    <path d="M10 20a2 2 0 004 0" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
  </svg>
);

/** Libellé d'un geste du client, dans le fil de la cloche. */
function libelleEvenement(e) {
  switch (e.type) {
    case 'devis_signe':
      return `Devis ${e.numero} signé`;
    case 'contrat_signe':
      return `Contrat ${e.numero} signé`;
    case 'devis_refuse':
      return `Devis ${e.numero} refusé`;
    case 'piece_deposee':
      return `Pièce déposée : ${e.detail || 'document'}`;
    case 'presence_confirmee':
      return `Présence confirmée : ${e.detail || ''}`;
    case 'absence_signalee':
      return `Absence signalée : ${e.detail || ''}`;
    default:
      return e.type;
  }
}

/**
 * Le moment d'une alerte, compté à l'affichage à partir de son échéance :
 * écrite lundi « dans 5 jours », elle dirait faux mercredi.
 */
function compteARebours(echeance) {
  if (!echeance) return '';
  const cible = new Date(`${echeance}T00:00:00`);
  const minuit = new Date();
  minuit.setHours(0, 0, 0, 0);
  const n = Math.round((cible - minuit) / 864e5);
  if (n === 0) return "aujourd'hui";
  if (n === 1) return 'demain';
  if (n > 1) return `dans ${n} jours`;
  if (n === -1) return 'hier';
  return `il y a ${-n} jours`;
}

/** Ce que dit une alerte, en tête : sa nature, lisible d'un coup d'œil. */
const NATURE_ALERTE = {
  formation_proche: 'Formation proche',
  planification_manquante: 'Planification à faire',
  contrat_non_signe: 'Contrat non signé',
  eleves_sans_email: 'Élus sans email',
  feuille_incomplete: 'Feuille incomplète',
};

/** « il y a 3 h », « hier », « 12/09 » : assez pour situer sans lire une date. */
function depuis(iso) {
  const d = new Date(iso);
  const h = (Date.now() - d.getTime()) / 36e5;
  if (h < 1) return `il y a ${Math.max(1, Math.round(h * 60))} min`;
  if (h < 24) return `il y a ${Math.round(h)} h`;
  if (h < 48) return 'hier';
  return d.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' });
}

/**
 * La cloche : ce que les clients ont fait de leur côté. Le compte vient des
 * pastilles (une seule source) ; le fil n'est chargé qu'à l'ouverture.
 */
function Cloche({ compte, onChange }) {
  const [ouverte, setOuverte] = useState(false);
  const [evenements, setEvenements] = useState([]);
  // Ce qui approche ou manque : la section du haut, la plus actionnable.
  const [alertes, setAlertes] = useState([]);
  const [enCours, setEnCours] = useState(false);
  // null tant qu'on ne sait pas ; '' si aucune adresse n'est configurée.
  const [emailNotif, setEmailNotif] = useState(null);
  // Où poser le panneau : sous la cloche, mesuré à l'ouverture.
  const bouton = useRef(null);
  const [position, setPosition] = useState({ top: 0, left: 0 });

  useEffect(() => {
    if (!ouverte) return undefined;
    let vivant = true;
    setEnCours(true);
    if (bouton.current) {
      const r = bouton.current.getBoundingClientRect();
      setPosition({ top: r.bottom + 8, left: Math.max(8, r.left - 8) });
    }
    Promise.all([
      getEvenementsClients()
        .then((res) => vivant && setEvenements(res.data || []))
        .catch(() => vivant && setEvenements([])),
      getAlertes()
        .then((res) => vivant && setAlertes(res.data || []))
        .catch(() => vivant && setAlertes([])),
    ]).finally(() => vivant && setEnCours(false));
    getParametresDevis()
      .then((res) => vivant && setEmailNotif(res.data?.emailNotifications || ''))
      .catch(() => vivant && setEmailNotif(null));
    return () => {
      vivant = false;
    };
  }, [ouverte]);

  async function marquer(cible) {
    setEnCours(true);
    try {
      const res = await marquerEvenementsVus(cible);
      setEvenements(res.data || []);
      if (onChange) onChange();
    } catch {
      /* la prochaine ouverture rattrapera */
    } finally {
      setEnCours(false);
    }
  }

  async function marquerAlertes(cible) {
    setEnCours(true);
    try {
      const res = await marquerAlertesVues(cible);
      setAlertes(res.data || []);
      if (onChange) onChange();
    } catch {
      /* la prochaine ouverture rattrapera */
    } finally {
      setEnCours(false);
    }
  }

  const nonVus = evenements.filter((e) => !e.vue_le);
  const vus = evenements.filter((e) => e.vue_le);
  const alertesNonVues = alertes.filter((a) => !a.vue_le);

  return (
    <div className="sidebar-cloche">
      <button
        ref={bouton}
        type="button"
        className={'sidebar-cloche-bouton' + (ouverte ? ' sidebar-cloche-bouton-active' : '')}
        onClick={() => setOuverte((v) => !v)}
        aria-label={compte > 0 ? `${compte} notification(s)` : 'Notifications'}
        aria-expanded={ouverte}
        title="Ce qui approche, et ce que les clients ont fait"
      >
        {CLOCHE_ICON}
        {compte > 0 && <span className="sidebar-notif sidebar-cloche-compte">{compte}</span>}
      </button>

      {ouverte &&
        createPortal(
          <>
            {/* Un voile transparent : cliquer ailleurs referme le panneau. */}
            <div className="sidebar-cloche-voile" onClick={() => setOuverte(false)} />
            <div
              className="sidebar-cloche-panneau"
              style={{ top: position.top, left: position.left }}
              role="dialog"
              aria-label="Gestes des clients"
            >
            {emailNotif === '' && (
              <div className="sidebar-cloche-note">
                Aucune adresse email ne reçoit ces notifications.{' '}
                <NavLink to="/parametrage/devis" onClick={() => setOuverte(false)}>
                  Renseignez-la dans Paramétrage › Devis
                </NavLink>
                .
              </div>
            )}
            <div className="sidebar-cloche-tete">
              <strong>À surveiller</strong>
              {alertesNonVues.length > 0 && (
                <button
                  type="button"
                  className="sidebar-cloche-lien"
                  onClick={() => marquerAlertes({})}
                  disabled={enCours}
                >
                  Tout marquer vu
                </button>
              )}
            </div>

            {!enCours && alertes.length === 0 && (
              <div className="sidebar-cloche-vide">Rien qui approche ni ne manque.</div>
            )}

            <ul className="sidebar-cloche-liste">
              {alertes.map((a) => (
                <li
                  key={`alerte-${a.id}`}
                  className={
                    'sidebar-cloche-item sidebar-cloche-alerte' +
                    ` sidebar-cloche-alerte-${a.type}` +
                    (a.vue_le ? ' sidebar-cloche-item-vu' : '')
                  }
                >
                  <div className="sidebar-cloche-texte">
                    <div className="sidebar-cloche-nature">
                      {NATURE_ALERTE[a.type] || a.type}
                      {a.echeance && <> · {compteARebours(a.echeance)}</>}
                    </div>
                    <div className="sidebar-cloche-libelle">{a.titre}</div>
                    {a.detail && <div className="sidebar-cloche-meta">{a.detail}</div>}
                  </div>
                  <div className="sidebar-cloche-actions">
                    {a.contact_id && (
                      <NavLink
                        to={`/demande-de-devis?contact=${a.contact_id}`}
                        className="sidebar-cloche-lien"
                        onClick={() => setOuverte(false)}
                      >
                        Dossier
                      </NavLink>
                    )}
                    {!a.vue_le && (
                      <button
                        type="button"
                        className="sidebar-cloche-lien"
                        onClick={() => marquerAlertes({ id: a.id })}
                        disabled={enCours}
                      >
                        Vu
                      </button>
                    )}
                  </div>
                </li>
              ))}
            </ul>

            <div className="sidebar-cloche-tete sidebar-cloche-tete-suite">
              <strong>Côté clients</strong>
              {nonVus.length > 0 && (
                <button
                  type="button"
                  className="sidebar-cloche-lien"
                  onClick={() => marquer({})}
                  disabled={enCours}
                >
                  Tout marquer vu
                </button>
              )}
            </div>

            {enCours && evenements.length === 0 && (
              <div className="sidebar-cloche-vide">Chargement…</div>
            )}
            {!enCours && evenements.length === 0 && (
              <div className="sidebar-cloche-vide">Rien de nouveau côté clients.</div>
            )}

            <ul className="sidebar-cloche-liste">
              {[...nonVus, ...vus].map((e) => (
                <li
                  key={`${e.type}-${e.id}`}
                  className={'sidebar-cloche-item' + (e.vue_le ? ' sidebar-cloche-item-vu' : '')}
                >
                  <div className="sidebar-cloche-texte">
                    <div className="sidebar-cloche-libelle">{libelleEvenement(e)}</div>
                    <div className="sidebar-cloche-meta">
                      {[e.prenom, e.nom].filter(Boolean).join(' ')}
                      {e.societe_nom ? ` · ${e.societe_nom}` : ''} · {depuis(e.quand)}
                    </div>
                  </div>
                  <div className="sidebar-cloche-actions">
                    {(e.type === 'presence_confirmee' || e.type === 'absence_signalee') && (
                      <NavLink
                        to={`/convocations?contact=${e.contact_id}&client=${encodeURIComponent(
                          e.societe_nom || ''
                        )}`}
                        className="sidebar-cloche-lien"
                        onClick={() => setOuverte(false)}
                      >
                        Convocations
                      </NavLink>
                    )}
                    {e.client_id && (
                      <NavLink
                        to={`/clients/${e.client_id}`}
                        className="sidebar-cloche-lien"
                        onClick={() => setOuverte(false)}
                      >
                        Dossier
                      </NavLink>
                    )}
                    {!e.vue_le && (
                      <button
                        type="button"
                        className="sidebar-cloche-lien"
                        onClick={() => marquer({ type: e.type, id: e.id })}
                        disabled={enCours}
                      >
                        Vu
                      </button>
                    )}
                  </div>
                </li>
              ))}
            </ul>
            </div>
          </>,
          document.body
        )}
    </div>
  );
}

/** Les sections de pastille que l'ouverture de chaque page marque vues. */
const SECTIONS_PAR_ROUTE = {
  '/demande-de-devis': ['demandes', 'devis'],
  '/contrats': ['contrats'],
  '/convocations': ['convocations'],
  '/rappels': ['rappels'],
  '/feuilles-de-presence': ['feuilles'],
  '/calendrier': ['agenda'],
};

function Sidebar({ ouvert = false, onFermer }) {
  const { pathname } = useLocation();
  const { utilisateur, deconnecter } = useAuth();
  const initiale = (utilisateur?.nom || 'B').trim().charAt(0).toUpperCase() || 'B';

  /* Ce qui attend un geste, page par page : les pastilles rouges de la
     barre. Relu à chaque changement de page — traiter puis naviguer les
     fait retomber, sans sondage. */
  const [pastilles, setPastilles] = useState({});

  // Appelable aussi par la cloche : marquer vu doit faire retomber son compte.
  const rechargerPastilles = () =>
    getNotifications()
      .then((res) => setPastilles(res.data || {}))
      .catch(() => {});

  useEffect(() => {
    let vivant = true;
    // Entrer dans une liste la marque vue : sa pastille retombe, puis ne compte
    // plus que ce qui arrive après. Le marquage précède la lecture des comptes.
    const sections = SECTIONS_PAR_ROUTE[pathname] || [];
    (sections.length ? marquerPastillesVues(sections).catch(() => {}) : Promise.resolve())
      .then(() => getNotifications())
      .then((res) => {
        if (vivant) setPastilles(res.data || {});
      })
      .catch(() => {
        /* pastilles muettes plutôt qu’une barre en erreur */
      });
    return () => {
      vivant = false;
    };
  }, [pathname]);

  // Quelle pastille nourrit quelle entrée du parcours.
  const PASTILLE_PAR_ROUTE = {
    // Demande de devis porte aussi le suivi des devis envoyés (ancienne entrée « Devis »).
    '/demande-de-devis': (pastilles.demandes || 0) + (pastilles.devis || 0),
    '/contrats': pastilles.contrats,
    '/convocations': pastilles.convocations,
    '/rappels': pastilles.rappels,
    '/feuilles-de-presence': pastilles.feuilles,
    // Tâches de l'agenda dues aujourd'hui ou en retard (migration 034).
    '/calendrier': pastilles.agenda,
  };

  return (
    <aside className={'sidebar' + (ouvert ? ' sidebar-ouverte' : '')}>
      <button type="button" className="sidebar-close" onClick={onFermer} aria-label="Fermer le menu">
        ×
      </button>

      <div className="sidebar-brand">
        <div className="sidebar-brand-logo">
          <img src={logoClair} alt="Studmin" />
          <div className="sidebar-brand-subtitle">GESTION INTERNE · ECHO CSE</div>
        </div>
        {/* La loupe : la recherche globale, en tête de page, prend le focus
            (le tiroir se referme en format mobile). */}
        <button
          type="button"
          className="sidebar-cloche-bouton"
          onClick={() => {
            if (onFermer) onFermer();
            window.dispatchEvent(new Event(EVENEMENT_RECHERCHE));
          }}
          title="Rechercher partout (Ctrl K)"
          aria-label="Rechercher partout"
          data-recherche-declencheur
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" aria-hidden="true">
            <circle cx="11" cy="11" r="7" />
            <path d="M20 20l-3.5-3.5" />
          </svg>
        </button>
        <Cloche
          compte={(pastilles.clients || 0) + (pastilles.alertes || 0)}
          onChange={rechargerPastilles}
        />
      </div>

      <nav className="sidebar-nav">
        <div className="sidebar-nav-label">PILOTAGE</div>
        <ul>
          {NAV_ITEMS.map((item) => (
            <EntreeParcours
              key={item.to}
              item={{ ...item, badge: PASTILLE_PAR_ROUTE[item.to] || 0 }}
            />
          ))}

          {/* Les tests de recrutement, juste avant les personnes qu'ils
              évaluent (clients, formateurs). */}
          <EntreeParcours item={QCM_ITEM} />
          {/* Signatures reçues et pas encore vues : elles se lisent sur la
              page Clients, c'est là que la pastille mène. */}
          <MenuDeplie
            libelle="Clients"
            icone={CLIENTS_ICON}
            options={CLIENTS_OPTIONS}
          />
          {/* Après les clients : ces pages s'adressent à des personnes, elles
              ne jalonnent pas le parcours d'un dossier. */}
          <EntreeParcours
            item={{
              ...FORMATEURS_ITEM,
              badge: PASTILLE_PAR_ROUTE[FORMATEURS_ITEM.to] || 0,
            }}
          />
          <MenuDeplie libelle="Paramétrage" icone={GEAR_ICON} options={PARAMETRAGE_OPTIONS} />
        </ul>
      </nav>

      <div className="sidebar-user">
        <div className="sidebar-user-avatar">{initiale}</div>
        <div className="sidebar-user-infos">
          <div className="sidebar-user-name" title={utilisateur?.nom}>
            {utilisateur?.nom || 'BO&CO Studio'}
          </div>
          <div className="sidebar-user-email" title={utilisateur?.email}>
            {utilisateur?.email || ''}
          </div>
        </div>
        {/* La session tombe côté serveur ; RequireAuth renvoie aussitôt à la
            page de connexion. */}
        <button
          type="button"
          className="sidebar-deconnexion"
          onClick={deconnecter}
          title="Se déconnecter"
          aria-label="Se déconnecter"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M16 17l5-5-5-5M21 12H9" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
      </div>
    </aside>
  );
}

export default Sidebar;

